/**
 * queryKeys — ScheduleDraft 与引用数据 React Query key 的命名锚点。
 *
 * 本模块集中定义 Feature 级服务端状态的缓存身份，避免 hooks、services 与
 * mutation 层各自拼接 key。ScheduleDraft 数据与 reference data 分属不同前缀，
 * 便于精确失效和跨模块共享引用数据。
 *
 * 设计文档 → docs/20_workspace_ux/28_frontend_code_map_and_data_flow.md
 * 骨架关联 → hooks/useScheduleDraftDetail.js (detail query), hooks/useScheduleDraftMutationHelper.js (缓存失效), services/scheduleDraftReferenceDataService.js (引用数据边界)
 */

export const scheduleDraftQueryKeys = {
    all: ["schedule-draft-v3"],
    lists: () => ["schedule-draft-v3", "list"],
    list: (params = {}) => [...scheduleDraftQueryKeys.lists(), params],
    details: () => ["schedule-draft-v3", "detail"],
    detail: (draftId) => [...scheduleDraftQueryKeys.details(), draftId],
    tracks: (draftId) => [...scheduleDraftQueryKeys.detail(draftId), "tracks"],
    flights: (draftId) => [...scheduleDraftQueryKeys.detail(draftId), "flights"],
    executionFlights: () => ["flight-operations"],
    matchesRoot: (draftId) => [...scheduleDraftQueryKeys.detail(draftId), "matches"],
    matches: (draftId, trackId, referenceTrackId) => [...scheduleDraftQueryKeys.matchesRoot(draftId), trackId, referenceTrackId || "current-baseline"],
    publications: (draftId, params = {}) => [...scheduleDraftQueryKeys.detail(draftId), "publications", params],
    publication: (draftId, publicationId) => [...scheduleDraftQueryKeys.detail(draftId), "publication", publicationId],
    operationTransactionSummaries: (operationIds = []) => ["schedule-draft-v3", "operation-transaction-summaries", [...operationIds].sort((left, right) => Number(left) - Number(right))],
    compareConfirmation: (draftId, trackId, referenceTrackId) => [...scheduleDraftQueryKeys.detail(draftId), "compare-confirmation", trackId, referenceTrackId || "current-baseline"],
    comparisonBaseline: (draftId, trackId) => [...scheduleDraftQueryKeys.detail(draftId), "comparison-baseline", trackId],
    analysis: (draftId, trackId) => [...scheduleDraftQueryKeys.detail(draftId), "analysis", trackId],
    agentSessions: (draftId) => [...scheduleDraftQueryKeys.detail(draftId), "agent-sessions"],
    agentStrategies: (agentName) => [...scheduleDraftQueryKeys.all, "agent-strategies", agentName],
};

export const referenceDataQueryKeys = {
    all: ["reference"],
    airports: () => ["reference", "airports"],
    aircrafts: () => ["reference", "aircrafts"],
    aircraftAvailabilityWindows: (registrations = [], start = "", end = "", status = "ACTIVE") => [
        "reference",
        "aircraftAvailabilityWindows",
        [...registrations].sort(),
        start || "",
        end || "",
        status || "all",
    ],
    flightLegs: () => ["reference", "flightLegs"],
    activeFlightRoutes: () => ["reference", "flightRoutes", "active"],
    flightRoute: (routeId) => ["reference", "flightRoute", routeId],
    flightRouteUsage: (aircraftReg) => ["reference", "flightRoutes", "usage", aircraftReg || "all"],
    flightRoutes: (departureAirport, arrivalAirport) => ["reference", "flightRoutes", departureAirport, arrivalAirport],
    airportConstraints: () => ["reference", "airportConstraints"],
    v1Schedules: (params = {}) => ["reference", "v1Schedules", params],
};

export default scheduleDraftQueryKeys;
